/**
 * =============================================================================
 * camera.js — KAMERA SIDE-SCROLLING
 * =============================================================================
 * Kamera TIDAK menampilkan seluruh lintasan sekaligus.
 * Ia mengikuti pemimpin balapan secara smooth + efek shake saat start/finish.
 *
 * Dipakai oleh: race.js (apply ke Canvas 2D context setiap frame)
 * =============================================================================
 */

import { CONFIG, clamp } from './config.js';

/**
 * Camera
 * ---------------------------------------------------------------------------
 * Properti penting:
 *   x, targetX  — posisi horizontal dunia yang sedang dilihat
 *   shake*      — offset getar sementara
 *   width/height — ukuran viewport (px layar)
 */
export class Camera {
  constructor() {
    this.x = 0;
    this.y = 0;
    this.targetX = 0;
    this.shake = 0;
    this.shakeX = 0;
    this.shakeY = 0;
    this.width = 800;
    this.height = 600;
  }

  /**
   * resize(w, h)
   * Sinkronkan ukuran viewport saat window/canvas berubah.
   */
  resize(w, h) {
    this.width = w;
    this.height = h;
  }

  /**
   * setImmediate(x)
   * Loncat kamera ke posisi X tanpa interpolasi (dipakai saat start race).
   */
  setImmediate(x) {
    this.x = x;
    this.targetX = x;
  }

  /**
   * followLeader(leaderX, trackLength)
   * Hitung target kamera agar pemimpin ada di ~35% kiri layar + look-ahead.
   * Dibatasi agar tidak melewati ujung lintasan.
   */
  followLeader(leaderX, trackLength) {
    const look = CONFIG.CAMERA.lookAhead;
    this.targetX = leaderX - this.width * 0.35 + look * 0.15;
    const maxX = Math.max(0, trackLength + 200 - this.width);
    this.targetX = clamp(this.targetX, 0, maxX);
  }

  /**
   * addShake(amount)
   * Picu getaran kamera (start gun, eliminasi, finish).
   */
  addShake(amount = 8) {
    this.shake = Math.max(this.shake, amount);
  }

  /**
   * update(dt)
   * Setiap frame: lerp menuju targetX + decay shake.
   */
  update(dt) {
    /* Exponential smoothing — terasa natural di semua framerate */
    const k = 1 - Math.exp(-CONFIG.CAMERA.lerp * dt);
    this.x += (this.targetX - this.x) * k;

    if (this.shake > 0.1) {
      this.shakeX = (Math.random() - 0.5) * this.shake * 2;
      this.shakeY = (Math.random() - 0.5) * this.shake * 2;
      this.shake *= Math.exp(-CONFIG.CAMERA.shakeDecay * dt);
    } else {
      this.shake = 0;
      this.shakeX = 0;
      this.shakeY = 0;
    }
  }

  /**
   * apply(ctx)
   * Geser context Canvas ke koordinat dunia (dipanggil sebelum draw arena/ants).
   */
  apply(ctx) {
    ctx.translate(-this.x + this.shakeX, this.shakeY);
  }

  /**
   * worldToScreen(wx)
   * Konversi koordinat dunia → X di layar (jarang dipakai, helper).
   */
  worldToScreen(wx) {
    return wx - this.x + this.shakeX;
  }

  /**
   * inView(wx, margin)
   * Lazy rendering: true jika objek di sekitar viewport (hemat draw).
   */
  inView(wx, margin = 80) {
    return wx > this.x - margin && wx < this.x + this.width + margin;
  }
}
